
import React, { useRef, useState } from 'react';
import { Search, Save, Undo2, Redo2, Plus, Loader2, Check, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useComponentSearch } from './hooks/useComponentSearch';
import { addComponentToCanvas } from './utils/addComponentToCanvas';
import { Node } from '@xyflow/react';
import { NodeData } from './initial-nodes';
import { useAuth } from '@/context/AuthContext';

interface ComponentSearchDropdownProps {
  setNodes: React.Dispatch<React.SetStateAction<Node<NodeData>[]>>;
  reactFlowInstance: any;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  selectedView: string;
  setSelectedView: (view: string) => void;
  onAddNew: () => void;
  autoSaveStatus?: { isSaving: boolean; lastSaved?: Date; error?: Error };
  diagramName?: string;
}

export function ComponentSearchDropdown({ 
  setNodes, 
  reactFlowInstance,
  onSave,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  selectedView,
  setSelectedView,
  onAddNew,
  autoSaveStatus,
  diagramName
}: ComponentSearchDropdownProps) {
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const { subscriptionData } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  
  const {
    searchTerm,
    setSearchTerm,
    results,
    loading,
    isOpen,
    setIsOpen,
    clearSearch
  } = useComponentSearch(subscriptionData?.subscription_id);

  const handleSelectComponent = (result: any) => {
    addComponentToCanvas(
      result,
      reactFlowInstance,
      setNodes,
      searchDropdownRef
    );

    clearSearch();
    setIsOpen(false);
  };

  const highlightSearchTerm = (text: string, term: string) => {
    if (!term) return text;

    const regex = new RegExp(`(${term})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => 
      regex.test(part) ? (
        <strong key={index} className="font-bold">
          {part}
        </strong>
      ) : (
        part
      )
    );
  };

  return (
    <div 
      ref={searchDropdownRef}
      className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] w-auto"
    >
      {/* Card wrapper */}
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        {isExpanded ? (
          <div className="flex flex-col gap-2">
            {/* Título do diagrama com botão de colapsar */}
            <div className="flex items-center justify-between gap-2">
              <h2 
                className="text-xs font-bold text-foreground text-center truncate flex-1" 
                title={diagramName}
              >
                {diagramName || 'Sem título'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="h-5 w-5 p-0 hover:bg-muted"
                title="Colapsar"
              >
                <ChevronUp size={14} />
              </Button>
            </div>
            
            {/* Linha de botões */}
            <div className="flex items-center gap-3">
            {/* Ícones de ação */}
            <Button
            variant="outline" 
            size="sm" 
            onClick={onSave} 
            className="h-10 w-10 p-0"
            title="Salvar diagrama"
          >
            <Save size={16} />
            <span className="sr-only">Salvar</span>
          </Button>

          {/* Indicador de autosave */}
          {autoSaveStatus && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/50 text-xs">
              {autoSaveStatus.isSaving ? (
                <>
                  <Loader2 size={14} className="animate-spin text-primary" />
                  <span className="text-muted-foreground">Salvando...</span>
                </>
              ) : autoSaveStatus.error ? (
                <>
                  <AlertCircle size={14} className="text-destructive" />
                  <span className="text-destructive">Erro ao salvar</span>
                </>
              ) : autoSaveStatus.lastSaved ? (
                <>
                  <Check size={14} className="text-green-600" />
                  <span className="text-muted-foreground">
                    Salvo às {autoSaveStatus.lastSaved.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </>
              ) : null}
            </div>
          )}

          <Button 
            variant="outline" 
            size="sm" 
            onClick={onAddNew} 
            className="h-10 w-10 p-0"
            title="Adicionar novo componente"
          >
            <Plus size={16} />
            <span className="sr-only">Adicionar componente</span>
          </Button>

          <Button 
            variant="outline" 
            size="sm" 
            onClick={onUndo} 
            disabled={!canUndo}
            className="h-10 w-10 p-0"
            title="Desfazer"
          >
            <Undo2 size={16} />
            <span className="sr-only">Desfazer</span>
          </Button>

          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRedo} 
            disabled={!canRedo}
            className="h-10 w-10 p-0"
            title="Refazer"
          >
            <Redo2 size={16} />
            <span className="sr-only">Refazer</span>
          </Button>


          {/* Dropdown de Visões */}
          <Select value={selectedView} onValueChange={setSelectedView}>
            <SelectTrigger className="w-48 h-10 bg-background border border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background border border-border shadow-lg z-50">
              <SelectItem value="visao-padrao">Visão Padrão</SelectItem>
              <SelectItem value="visao-codigo">Visão Código</SelectItem>
              <SelectItem value="visao-deploy">Visão Deploy</SelectItem>
              <SelectItem value="visao-performance">Visão Performance</SelectItem>
            </SelectContent>
          </Select>
            </div>
          </div>
        ) : (
          /* Versão colapsada - apenas título */
          <div className="flex items-center gap-2 py-1 px-2">
            <h2 
              className="text-xs font-bold text-foreground truncate max-w-[180px]" 
              title={diagramName}
            >
              {diagramName || 'Sem título'}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(true)}
              className="h-5 w-5 p-0 hover:bg-muted"
              title="Expandir"
            >
              <ChevronDown size={14} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
